import { useEffect, useState } from 'react';
import { Fingerprint, KeyRound, Loader2, ShieldAlert, Trash2, CreditCard } from 'lucide-react';
import { getElectronAPI, isDesktopApp } from '@/desktop/electronApi';
import type { HardwareCredType, LocalCashierPublicProfile } from '@/desktop/types';
import EnrollPinModal from '@/components/desktop/EnrollPinModal';
import {
  canUsePlatformBiometrics,
  enrollPlatformFingerprint,
} from '@/desktop/fingerprintWebAuthn';
import { useAuthStore } from '@/store/authStore';
import { useSyncStore } from '@/store/syncStore';
import { startFreshOnThisPc } from '@/desktop/startFresh';
import { isCompanyAdmin } from '@/types';

/**
 * Admin desktop unlock / enrollment policy panel (Settings → POS Rules).
 * Wipe requires online + admin confirmation.
 */
export default function DesktopUnlockSettings() {
  const api = getElectronAPI();
  const { user, role, company } = useAuthStore();
  const isOnline = useSyncStore((s) => s.isOnline && s.isServerReachable);
  const [profiles, setProfiles] = useState<LocalCashierPublicProfile[]>([]);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [hwToken, setHwToken] = useState('');
  const [hwType, setHwType] = useState<HardwareCredType>('rfid');
  const [hwLocalId, setHwLocalId] = useState('');

  const refresh = async () => {
    if (!api) return;
    setProfiles(await api.vault.listProfiles(company?.code));
  };

  useEffect(() => {
    if (isDesktopApp()) void refresh();
  }, []);

  if (!isDesktopApp() || !api) return null;

  const wipe = async () => {
    if (!isCompanyAdmin(role)) {
      setErr('Only Company Admin can wipe device unlock profiles.');
      return;
    }
    if (!isOnline) {
      setErr('Device wipe requires an online cloud session.');
      return;
    }
    if (
      !window.confirm(
        'Factory reset this PC? Clears PIN unlocks, unbinds company, and requires full desktop setup again. Local IndexedDB for this company should be cleared after confirm.',
      )
    ) {
      return;
    }
    setBusy(true);
    setErr('');
    try {
      await startFreshOnThisPc();
      setMsg('Device reset. Sign in online to run setup again.');
      await refresh();
      window.location.hash = '#/login';
      window.location.reload();
    } catch (e) {
      setErr((e as Error)?.message || 'Wipe failed');
    } finally {
      setBusy(false);
    }
  };

  const addFingerprint = async (localId: string) => {
    if (!canUsePlatformBiometrics()) {
      setErr('Platform biometrics unavailable');
      return;
    }
    setBusy(true);
    setErr('');
    try {
      const cred = await enrollPlatformFingerprint(localId, user?.full_name || 'Cashier');
      await api.vault.setFingerprint(localId, cred);
      setMsg('Fingerprint enrolled');
      await refresh();
    } catch (e) {
      setErr((e as Error)?.message || 'Fingerprint enroll failed');
    } finally {
      setBusy(false);
    }
  };

  const enrollHw = async () => {
    if (!hwLocalId || !hwToken.trim()) {
      setErr('Select cashier and enter card / fob UID');
      return;
    }
    setBusy(true);
    setErr('');
    try {
      await api.vault.enrollHardware(hwLocalId, hwType, hwToken.trim());
      setMsg(`${hwType.toUpperCase()} enrolled`);
      setHwToken('');
      await refresh();
    } catch (e) {
      setErr((e as Error)?.message || 'Hardware enroll failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-slate-50 p-4 space-y-3 mt-4">
      <div className="flex items-start gap-2">
        <KeyRound className="w-4 h-4 text-primary-600 mt-0.5" />
        <div>
          <p className="font-medium text-gray-900 text-sm">Desktop unlock (this PC)</p>
          <p className="text-xs text-gray-500">
            After cloud login, cashiers unlock offline with PIN / fingerprint / card. Company:{' '}
            {company?.code || '—'}. Prefer Offline Always or Local First for shop PCs.
          </p>
        </div>
      </div>

      <div className="space-y-1">
        {profiles.length === 0 && (
          <p className="text-xs text-gray-500">No cashiers enrolled yet.</p>
        )}
        {profiles.map((p) => (
          <div
            key={p.localId}
            className="flex flex-wrap items-center gap-2 text-xs bg-white border border-gray-100 rounded-lg px-3 py-2"
          >
            <span className="font-medium text-gray-800 flex-1">{p.displayName}</span>
            <span className="text-gray-400">{p.role}</span>
            <span className="text-gray-400">PIN {p.pinLength}d</span>
            {p.fingerprintEnabled && <Fingerprint className="w-3.5 h-3.5 text-emerald-600" />}
            {(p.hasMsr || p.hasRfid || p.hasFob) && <CreditCard className="w-3.5 h-3.5 text-sky-600" />}
            {!p.fingerprintEnabled && canUsePlatformBiometrics() && (
              <button
                type="button"
                disabled={busy}
                onClick={() => void addFingerprint(p.localId)}
                className="text-primary-700 hover:underline"
              >
                Add fingerprint
              </button>
            )}
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setEnrollOpen(true)}
          className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary-600 text-white"
        >
          Enroll / update my PIN
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => void wipe()}
          className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-red-200 text-red-700 bg-red-50 inline-flex items-center gap-1 disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
          Wipe / factory reset device
        </button>
      </div>

      <div className="border-t border-gray-200 pt-3 space-y-2">
        <p className="text-xs font-medium text-gray-700 flex items-center gap-1">
          <CreditCard className="w-3.5 h-3.5" /> Card / fob enrollment (phase-2 hooks)
        </p>
        <div className="flex flex-wrap gap-2 items-center">
          <select
            className="input-field text-xs py-1"
            value={hwLocalId}
            onChange={(e) => setHwLocalId(e.target.value)}
          >
            <option value="">Cashier…</option>
            {profiles.map((p) => (
              <option key={p.localId} value={p.localId}>
                {p.displayName}
              </option>
            ))}
          </select>
          <select
            className="input-field text-xs py-1"
            value={hwType}
            onChange={(e) => setHwType(e.target.value as HardwareCredType)}
          >
            <option value="rfid">RFID / key card</option>
            <option value="fob">Key fob</option>
            <option value="msr">MSR swipe token</option>
          </select>
          <input
            className="input-field text-xs py-1 flex-1 min-w-[120px]"
            placeholder="UID / swipe data"
            value={hwToken}
            onChange={(e) => setHwToken(e.target.value)}
          />
          <button type="button" onClick={() => void enrollHw()} className="text-xs px-2 py-1 border rounded-lg">
            Enroll
          </button>
        </div>
      </div>

      {!isOnline && (
        <p className="text-xs text-amber-700 flex items-center gap-1">
          <ShieldAlert className="w-3.5 h-3.5" /> Online required to wipe unlocks or change sync mode safely.
        </p>
      )}
      {msg && <p className="text-xs text-emerald-700">{msg}</p>}
      {err && <p className="text-xs text-red-600">{err}</p>}

      <EnrollPinModal open={enrollOpen} onClose={() => setEnrollOpen(false)} onEnrolled={() => void refresh()} />
    </div>
  );
}
